import React from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/styles'
import { Skeleton } from '@material-ui/lab'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import { scrollBar } from 'styles/theme'
import { useFetchGreyfinchSummaryQuery } from 'store/modules/Greyfinch'
import TxPlanSection from './TxPlanSection'
import AppointmentsSection from './AppointmentsSection'
import { GreyfinchPanelState, resolveGreyfinchPanelState } from './greyfinchPanelState'

const BASE_KEY = 'pages.patients.patientSidebar.items.greyfinch'

// Muted/secondary text grey already used across the app (e.g. empty states, pinned notes).
const MUTED_COLOR = '#727B8C'

const useStyles = makeStyles(() => ({
  root: {
    background: 'white',
    height: '100%',
    overflowY: 'auto',
    padding: '20px 24px',
    ...scrollBar
  },
  title: {
    marginBottom: 16
  }
}))

// Container for the per-patient Greyfinch panel. Linking is orchestrated on Greyfinch's side
// (Sync patient-match), so there is no link mutation here — an unlinked patient just renders a
// quiet "not linked yet" state. Extra props (e.g. hideLeftBorder from the switchable view) are ignored.
const GreyfinchIntegration = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const grinPatientId = useSelector(state => state.patientsReducer.patient?.id)

  const { data, isFetching, isError, isUninitialized, error } = useFetchGreyfinchSummaryQuery(
    { grinPatientId },
    { skip: !grinPatientId, refetchOnMountOrArgChange: true }
  )

  const renderBody = () => {
    const state = resolveGreyfinchPanelState({ grinPatientId, isUninitialized, isFetching, isError, error, data })

    switch (state) {
      case GreyfinchPanelState.Loading:
        return <Skeleton />
      case GreyfinchPanelState.NotConnected:
        return <DazzedParagraph14 color={MUTED_COLOR}>{t(`${BASE_KEY}.states.notConnected`)}</DazzedParagraph14>
      case GreyfinchPanelState.Unavailable:
        return <DazzedParagraph14 color={MUTED_COLOR}>{t(`${BASE_KEY}.states.unavailable`)}</DazzedParagraph14>
      case GreyfinchPanelState.NotLinked:
        return <DazzedParagraph14 color={MUTED_COLOR}>{t(`${BASE_KEY}.states.notLinked`)}</DazzedParagraph14>
      case GreyfinchPanelState.Populated:
        // `data` is guaranteed present only here — every other state must NOT dereference it. Keep Populated
        // an explicit case (not `default`) so a future enum value can't fall through and white-screen.
        return (
          <>
            <TxPlanSection txPlan={data.txPlan} />
            <AppointmentsSection upcoming={data.appointments?.upcoming} past={data.appointments?.past} />
          </>
        )
      default:
        return null
    }
  }

  // The panel title is a persistent header (matches the reference); the body switches per state.
  return (
    <div className={classes.root}>
      <DazzedParagraph14 bold color={MUTED_COLOR} className={classes.title}>
        {t(`${BASE_KEY}.panelTitle`)}
      </DazzedParagraph14>
      {renderBody()}
    </div>
  )
}

export default GreyfinchIntegration
